import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CustomTranslationsService,
  SCHEDULE_HINT_INFO,
} from '../../../../operational-data-common/translations';
import { PeriodService, ScheduleEntry, ScheduleService } from '../../services';
import { TranslateService } from '@ngx-translate/core';
import { InstallationFeaturesStore } from '@store';

@Component({
  selector: 'app-program-period-dialog',
  templateUrl: './program-period-dialog.component.html',
  styleUrls: ['./program-period-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgramPeriodDialogComponent implements OnInit, OnDestroy {
  dataDialog: {
    schedule: ScheduleEntry[];
    dayIds: number[];
  };

  isNewPeriod: boolean;
  defaultPeriod: ScheduleEntry;
  newPeriod: ScheduleEntry;

  periods: ScheduleEntry[];
  programs$: BehaviorSubject<{ value: string; css?: string }[]>;
  offSetDeviceTime$: Observable<Date>;

  resolution: number;
  maxEntries: number;
  defaultProgram: string;
  defaultMode: string;

  days: string[];
  history: ScheduleEntry[][] = [];

  canAddNewPeriod: boolean;
  canShowScheduleHintBox: boolean;
  scheduleHintMessage: string;
  maxEntriesOverflowAttempt: boolean;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ProgramPeriodDialogComponent>,
    public scheduleService: ScheduleService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private periodService: PeriodService,
    private customTranslationsService: CustomTranslationsService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.history.push(cloneDeep<ScheduleEntry[]>(this.dataDialog.schedule));
    this.periods = this.dataDialog.schedule;
    this.maxEntries = this.scheduleService.maxEntries;
    this.programs$ = this.periodService.programs$;
    this.days = this.scheduleService.days;
    this.resolution = this.scheduleService.resolution;
    this.defaultMode = this.scheduleService.defaultMode;
    this.offSetDeviceTime$ = this.installationFeaturesStore.getOffsetDeviceTime();

    this.subscribeToPrograms();
    this.updateNewPeriodValue();
    this.updateScheduleAndPeriodFlags();
    this.initScheduleHintMessage();
  }

  ngOnDestroy(): void {
    this.history.length = 0;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onProgramChange(period: ScheduleEntry, isNewPeriod = false): void {
    if (isNewPeriod) {
      this.newPeriod = { ...period };
      this.autoApplyNewPeriodMerge();

      return;
    }
    const newPeriods = this.periodService.recalculatePeriodsByProgramChange(this.periods, period);
    this.updatePeriods(newPeriods);
  }

  onStartTimeChange(period: ScheduleEntry, isNewPeriod = false): void {
    if (isNewPeriod) {
      this.newPeriod = { ...period };
      this.autoApplyNewPeriodMerge();

      return;
    }
    const newPeriods = this.periodService.recalculatePeriodsByStartTimeChange(this.periods, period);
    this.updatePeriods(newPeriods);
  }

  onEndTimeChange(period: ScheduleEntry, isNewPeriod = false): void {
    if (isNewPeriod) {
      this.newPeriod = { ...period };
      this.autoApplyNewPeriodMerge();

      return;
    }
    const newPeriods = this.periodService.recalculatePeriodsByEndTimeChange(this.periods, period);
    this.updatePeriods(newPeriods);
  }

  onUndo(): void {
    if (this.canUndo()) {
      this.history.pop();
      this.periods = cloneDeep<ScheduleEntry[]>(this.history[this.history.length - 1]);
      this.updateNewPeriodValue();
      this.updateScheduleAndPeriodFlags();
    }
  }

  onDelete(period: ScheduleEntry): void {
    const newPeriods = this.periodService.recalculatePeriodsByRemoveOne(this.periods, period);
    this.updatePeriods(newPeriods);
  }

  autoApplyNewPeriodMerge(): void {
    if (this.isNewPeriodReady()) {
      this.onApplyNewPeriod();
    }
  }

  onApplyNewPeriod(): void {
    const newPeriods = this.periodService.recalculatePeriodsByAddOne(this.periods, this.newPeriod);
    this.maxEntriesOverflowAttempt =
      this.maxEntries < newPeriods?.filter((period) => period.mode !== this.defaultMode)?.length;
    if (!this.maxEntriesOverflowAttempt) {
      this.isNewPeriod = false;
      this.updatePeriods(newPeriods);
      this.updateNewPeriodValue();
    } else {
      this.updateNewPeriodValue(this.newPeriod.mode);
    }
  }

  onAddNewPeriod(): void {
    this.maxEntriesOverflowAttempt = false;
    this.isNewPeriod = true;
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  onSave(): void {
    this.dialogRef.close({ action: 'save', periods: this.history.slice(-1) });
  }

  canDelete(period: ScheduleEntry): boolean {
    return this.periods.length > 1 || period.mode !== this.defaultMode;
  }

  canUndo(): boolean {
    return this.history.length > 1;
  }

  addAnd(id: number): boolean {
    const len = this.dataDialog.dayIds.length;

    return id === len - 1 && len > 1;
  }

  addComma(id: number): boolean {
    const len = this.dataDialog.dayIds.length;

    return id < len - 2;
  }

  private updatePeriods(periods: ScheduleEntry[]): void {
    const p = cloneDeep<ScheduleEntry[]>(periods);
    this.history.push(cloneDeep(p));
    this.periods = p;
    this.updateScheduleAndPeriodFlags();
    this.updateNewPeriodValue();
  }

  removeNewPeriod(): void {
    this.isNewPeriod = false;
    this.updateNewPeriodValue();
  }

  private subscribeToPrograms(): void {
    this.scheduleService.programs$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((programs) => {
      const _programs = [];
      programs.forEach((program) => {
        if (program.name !== this.defaultMode) {
          _programs.push({
            value: program.name,
            css: program.css,
          });
        }
      });

      this.defaultProgram = programs.find(
        (program) => program.name === this.scheduleService.defaultProgram
      )
        ? this.scheduleService.defaultProgram
        : programs[0].name;

      this.periodService.programs$.next(_programs);

      this.periodService.defaultMode = this.isNewPeriod ? this.defaultProgram : this.defaultMode;
      this.defaultPeriod = {
        start: '',
        end: '',
        mode: '',
      };
    });
  }

  private updateScheduleAndPeriodFlags(): void {
    this.canShowScheduleHintBox =
      this.periods?.some((period) => period.mode !== this.defaultMode) || this.isNewPeriod;
    const entries = this.periods?.filter((period) => period.mode !== this.defaultMode);
    this.canAddNewPeriod = !this.isNewPeriod && this.maxEntries > entries?.length;
  }

  private initScheduleHintMessage(): void {
    this.scheduleHintMessage = this.translateService.instant(
      SCHEDULE_HINT_INFO[this.scheduleService.scheduleType],
      {
        defaultMode: this.translateService.instant(
          this.customTranslationsService.OPERATING_PROGRAMS[this.scheduleService.defaultMode]
        ),
      }
    );
  }

  private updateNewPeriodValue(mode?: string): void {
    const nonDefaultPeriods = this.periods?.filter(
      (period: ScheduleEntry) => period.mode !== this.scheduleService.defaultMode
    );
    if (nonDefaultPeriods?.length) {
      this.newPeriod = {
        ...this.defaultPeriod,
        start: nonDefaultPeriods[nonDefaultPeriods.length - 1].end,
        mode: mode ?? this.defaultPeriod.mode,
      };
    } else {
      this.newPeriod = {
        ...this.defaultPeriod,
        start: '00:00',
      };
    }
  }

  private isNewPeriodReady(): boolean {
    return !!this.newPeriod.start && !!this.newPeriod.end && !!this.newPeriod.mode;
  }
}
